import { Fragment, type ReactNode } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import type { Finding, Review } from '../lib/api.ts'
import { icons } from './icons.ts'
import {
  ITEM_SECTIONS,
  type ItemSection,
  activesBoard,
  strayCompositionFindings,
  columnStatusFor,
  reasonFor,
  checkSides,
  itemSummary,
  needsPerson,
  worstStatus,
  type VerificationItem,
} from '../lib/verificationItems.ts'
import { CHECK_LABEL, label } from '../lib/vocabulary.ts'
import { sectionFor } from '../lib/rulePlacement.ts'
import { ruleHint, ruleIngredients, ruleReason, rulesOnActives } from '../lib/activeRules.ts'
import { ActiveRuleNotes } from './ActiveRuleNotes.tsx'
import { ReasonIcon } from './ReasonIcon.tsx'
import { CorrectionAsked } from './CorrectionAsked.tsx'
import { preparationsLabel } from '../lib/preparations.ts'
import { sessionIsSuperAdmin } from '../lib/session.ts'
import { Tag } from './Tag.tsx'
import { Tooltip } from './Tooltip.tsx'
import { FOCUS_RING_ROW } from './focusRing.ts'
import { canTeach, DecisionActions } from './DecisionActions.tsx'
import { LabelPrint } from './LabelPrint.tsx'
import {
  LABEL_FIELD_LABEL,
  LABEL_HEADINGS,
  labelField,
  labelStandIn,
  sortLabelFindings,
} from '../lib/labelFindings.ts'
import {
  ComparisonBoard,
  ComparisonHeader,
  ComparisonRow,
  DiffColumns,
  ReasonSlot,
  SectionLabel,
  type ColumnStatus,
} from './ComparisonColumns.tsx'

/**
 * One card: a title, what it concluded in a line, then its sections as two
 * columns each.
 *
 * Ported from V1's `ComparisonItem`. Two decisions carried over that V2 had
 * got wrong on its own:
 *
 * **Four sections, not nine.** FORMA, ATIVOS, POSOLOGIA, VOLUME. V2 rendered a
 * panel per scored check, which turned one preparation into a wall of
 * near-identical boxes. The checks are grouped, not dropped.
 *
 * **No prose.** V1 shows the two values and lets the colour say the rest.
 * V2 printed the explainer's sentence under every finding -- "a identidade do
 * ingrediente está consistente com a substância prescrita, nenhuma ação
 * necessária" -- which is a paragraph to say what an untinted row already
 * said. The messages are still written and still stored; they are simply not
 * what this screen is for.
 */

export interface ComparisonCardProps {
  readonly title: string
  /**
   * Only these findings, by fingerprint (#439): the Correção view, which shows
   * what was marked *Corrigir no sistema* and nothing to read past. Sections
   * and ativos without one are left out. Absent, everything.
   */
  readonly focus?: ReadonlySet<string> | undefined
  /** A tag beside the title: which card of the item this is. */
  readonly badge?: string | undefined
  /** Small text beside the summary, e.g. the ERP's own item number. */
  readonly meta?: string
  readonly findings: readonly Finding[]
  /** Present for a preparation; absent for the identity rows. */
  readonly item?: VerificationItem | undefined
  /** Which of the item's sections to draw: the formula's, or its rótulo's. */
  readonly sections?: readonly ItemSection[] | undefined
  readonly decisions: ReadonlyMap<string, Review>
  readonly busy: boolean
  /**
   * Nothing may be answered any more: the conferência is concluded, or it
   * is waiting on a correction and the thing to do is look again. The rows
   * still read; they stop being stops and lose their buttons.
   */
  readonly readOnly?: boolean
  readonly onDecision: (
    fingerprint: string,
    decision: string,
    options?: {
      synonymTerm?: string | undefined
      notes?: string | undefined
      /** Why, in Galdino's wording. See `REASONS`. */
      reason?: string | undefined
    },
  ) => void
  /** Withdraws a decision. A decision a pharmacist cannot take back is a trap. */
  readonly onClearDecision: (fingerprint: string) => void
  /**
   * Pointing at a row shows it on the page without choosing it.
   *
   * Separate from `onLocate` because a preview must not disturb what is
   * pinned: moving the mouse away puts the pinned row back, and a reader who
   * pinned something keeps it while glancing at its neighbours.
   */
  readonly onPreview?:
    | ((key: string, regions: readonly string[] | undefined, tone: 'error' | 'brand') => void)
    | undefined
  /**
   * The row being pointed at, wherever the pointer is.
   *
   * Hovering the prescription marks its row here too, so the two sides always
   * agree about which one thing is under discussion -- a highlight on the
   * page with nothing marked beside it leaves the reader to find it.
   */
  readonly previewKey?: string | undefined
  /**
   * Which row is being shown on the prescription, and how to change it.
   *
   * A step's regions drawn together overlap -- a preparation's ingredients sit
   * on consecutive lines of one paragraph, so the boxes stack and the frame
   * zooms out to hold all of them. Choosing one row is how you get a rectangle
   * around the thing you are actually reading.
   */
  /**
   * Every row pinned, not one.
   *
   * A comparison is often two rows read together -- an ingredient and the
   * volume it fills -- and one selection at a time made that a matter of
   * remembering where the last one was. The viewer frames the union of
   * whatever it is given, so several pins zoom to cover all of them.
   */
  readonly located?: ReadonlySet<string> | undefined
  /**
   * The product codes the catalogue holds, so a name links only where the
   * link works. Absent means link nothing, which is right for a screen that
   * was not told.
   */
  readonly knownProducts?: ReadonlySet<string> | undefined
  /** The product each keyed ingredient was keyed with, by ingredient id. */
  readonly keyedCodes?: Readonly<Record<string, string>> | undefined
  /**
   * Overrides the ERP's type for a keyed line the receita never named, making
   * its product an excipient (ADR 0014). Offered as a reason under ✗, never as
   * a control of its own; absent, the reason is not offered.
   */
  readonly onMarkExcipient?:
    | ((ingredientId: string, productName: string, fingerprints: readonly string[]) => void)
    | undefined
  readonly onLocate?: (
    key: string | undefined,
    regions: readonly string[],
    tone: 'error' | 'brand',
  ) => void
}

export function ComparisonCard({
  title,
  badge,
  meta,
  findings,
  item,
  sections = ITEM_SECTIONS,
  decisions,
  busy,
  readOnly = false,
  onDecision,
  onClearDecision,
  located = new Set<string>(),
  knownProducts = new Set<string>(),
  keyedCodes = {},
  onMarkExcipient,
  previewKey,
  onLocate,
  onPreview,
  focus,
}: ComparisonCardProps) {
  const inFocus = (f: Finding) => focus === undefined || focus.has(f.fingerprint)
  // In the Correção view, what was asked under the row it was asked about.
  const asked = (rowFindings: readonly Finding[]) =>
    focus === undefined ? null : (
      <CorrectionAsked findings={rowFindings.filter(inFocus)} decisions={decisions} />
    )
  // Only what a person has to answer gets controls. `ok` and `info` are
  // something we checked and something we noticed; neither is work.
  const decidable = readOnly
    ? []
    : findings.filter((f) => f.disposition === 'divergence' || f.disposition === 'undecided')

  // Which section draws a finding (#549): a rule goes with what it is about.
  const onActives = item === undefined ? new Set<string>() : rulesOnActives(activesBoard(item).rows)
  const homeOf = (f: Finding) => sectionFor(f, sections, onActives)
  const answeredKeys = new Set(decisions.keys())
  // On a step with no formula: the rules about the whole requisição.
  const wideRules = item === undefined ? findings.filter((f) => f.check === 'operational') : []

  // The ✓/✗ of a row on a step with no formula, quiet where nothing is asked.
  const decisionFor = (finding: Finding) =>
    readOnly ? undefined : (
      <DecisionActions
        quiet={!decidable.includes(finding)}
        finding={finding}
        recorded={decisions.get(finding.fingerprint)}
        busy={busy}
        onDecision={onDecision}
        onClear={onClearDecision}
      />
    )

  /*
   * What one press on a row answers.
   *
   * Everything on it, until part of it has been answered: from then on, only
   * what is left. A forma answered "é um sinônimo" and then joined by a rule
   * nobody had answered drew one control for both, and pressing it for the
   * rule wrote over the forma's answer -- its reason and its synonym with it.
   */
  const stillOpen = (asking: readonly Finding[]) =>
    asking.every((f) => decisions.has(f.fingerprint))
      ? asking
      : asking.filter((f) => !decisions.has(f.fingerprint))

  /*
   * One ✓/✗ for a row that several findings ride on, as an ativo's has: the
   * comparison first, the rules about the same thing alongside it.
   */
  const decisionForAll = (rowFindings: readonly Finding[]) => {
    const asking = stillOpen(rowFindings.filter((f) => decidable.includes(f)))
    const primary = asking[0]
    /*
     * Nothing to answer, still markable: the forma, apresentação and
     * posologia rows lost their "marcar divergência" when rules started
     * riding on them (#549), so an error the comparison missed there had
     * nowhere to be said. The comparison is the row's subject, so it is
     * what the mark lands on.
     */
    if (primary === undefined) return decisionFor(rowFindings[0]!)
    return (
      <DecisionActions
        finding={primary}
        alongside={asking.slice(1)}
        // Pressed only once the whole row is answered.
        recorded={
          asking.every((f) => decisions.has(f.fingerprint))
            ? decisions.get(primary.fingerprint)
            : undefined
        }
        busy={busy}
        onDecision={onDecision}
        onClear={onClearDecision}
      />
    )
  }

  /*
   * Rules, as rows of their own: a rule has no two sides.
   *
   * The comparison boards put what the receita says beside what was keyed. A
   * rule asks a question of the requisition itself -- the wrong filial, a
   * preference not met -- so drawn as a comparison it would be two dashes and
   * a decision button, which reads as a difference nobody can see.
   *
   * Each wears its own colour, as each ativo does: the board used to wear the
   * worst for all of them, so one rule that asked painted the reminder beside
   * it red as well. And each that asks is a stop of the keyboard pass, like
   * every other apontamento -- these were the one kind Tab walked past.
   */
  const ruleBoard = (rules: readonly Finding[]) => (
    <ComparisonBoard status={worstStatus(rules, answeredKeys)} fillRows>
      {rules.filter(inFocus).map((finding) => {
        const tone = columnStatusFor(finding.disposition, decisions.has(finding.fingerprint))
        return (
          <div key={finding.fingerprint} className={rowFill(tone)}>
            <Locatable
              itemKey={finding.fingerprint}
              regions={finding.regions}
              tone={tone}
              located={located}
              onLocate={onLocate}
              onPreview={onPreview}
              previewed={previewKey === finding.fingerprint}
              needsDecision={decidable.includes(finding) && !decisions.has(finding.fingerprint)}
              answered={decidable.includes(finding) && decisions.has(finding.fingerprint)}
            >
              <RuleRow
                finding={finding}
                answered={decisions.has(finding.fingerprint)}
                actions={decisionFor(finding)}
              />
            </Locatable>
            {asked([finding])}
          </div>
        )
      })}
    </ComparisonBoard>
  )

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-col">
        <div className="flex flex-wrap items-center gap-2">
          {badge !== undefined && <Tag tone="brand">{badge}</Tag>}
          <span className="text-tH6 text-text-strong">{title}</span>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {meta !== undefined && (
            <>
              <span className="text-tSmall text-text-weak">{meta}</span>
              <span aria-hidden="true" className="text-stroke-weak">
                |
              </span>
            </>
          )}
          <span className="text-tP text-text-weak">{itemSummary(findings)}</span>
        </div>
      </div>

      {item === undefined
        ? /*
           * Identity rows: one board per finding, each under its own label.
           *
           * The card's title used to name what was compared, when patient and
           * prescriber were a step each. Combined into "Dados cadastrais", the
           * title names both and the two boards sat unlabelled one above the
           * other -- two names with nothing to say which was the patient. The
           * same small label the preparation sections wear, and the same words
           * the sem-receita view already uses.
           */
          [
            ...findings
              .filter((f) => f.check !== 'operational')
              .filter(inFocus)
              .map((finding) => (
                <div key={finding.fingerprint} className="flex flex-col gap-2">
                  {CHECK_LABEL[finding.check] !== undefined && (
                    <SectionLabel
                      label={
                        // Which preparations, when the orçamento is for more than one
                        // person (#342): "Paciente · preparações 1 e 3".
                        [CHECK_LABEL[finding.check]!, preparationsLabel(finding.evidence)]
                          .filter((part) => part !== undefined)
                          .join(' · ')
                      }
                    />
                  )}
                  <CheckBoard
                    finding={finding}
                    readOnly={readOnly}
                    answered={decisions.has(finding.fingerprint)}
                    located={located}
                    onLocate={onLocate}
                    onPreview={onPreview}
                    previewKey={previewKey}
                    actions={decisionFor(finding)}
                  />
                  {asked([finding])}
                </div>
              )),
            /*
             * The rules about the requisition as a whole, as one list (#545,
             * #549). A rule has no two sides here either, and through
             * CheckBoard came out as two dashes. Each used to stand in a board
             * of its own under the label "Regra operacional", so a step
             * titled "Regras do pedido" said so again above every line of it;
             * the label is drawn once, and only where the step holds
             * something that is not a rule.
             */
            wideRules.some(inFocus) && (
              <div key="rules" className="flex flex-col gap-2">
                {wideRules.length < findings.length && <SectionLabel label="Regras operacionais" />}
                {ruleBoard(wideRules)}
              </div>
            ),
          ]
        : sections.map((section) => {
            const own = findings.filter((f) => homeOf(f) === section)
            // A comparison section also holds the rules about its subject.
            const compared = own.filter((f) => f.check !== 'operational')
            const rules = own.filter((f) => f.check === 'operational')
            const riding = rules.filter((f) => needsPerson(f.disposition))
            const reminders = rules.filter((f) => !needsPerson(f.disposition))

            // Only failures are reported at all, so an empty rules board would
            // say nothing rather than "examined and fine".
            if (section.onlyWhenPresent === true && own.length === 0) return null
            // In focus, a section is shown only for what it was asked about.
            if (focus !== undefined && !own.some(inFocus)) return null

            /*
             * All four sections, always, as V1 renders them.
             *
             * A section with no findings used to disappear, so a card's shape
             * changed from item to item and "nothing to say about the volume"
             * looked identical to "this preparation has no volume". A
             * pharmacist comparing two items could not tell which checks
             * had been examined; an empty row with a dash says both.
             */

            return (
              <div key={section.label} className="flex flex-col gap-2">
                {/* The Rótulo card's badge already names it. */}
                {section.label !== 'Rótulo' && <SectionLabel label={section.label} />}
                {section.label === 'Regras operacionais' ? (
                  ruleBoard(own)
                ) : section.label === 'Rótulo' ? (
                  /*
                   * The label itself, then what was checked on it.
                   *
                   * The print comes first because it is what the conferente
                   * is signing off on; each row under it names the part of
                   * the label it compared and against what -- the receita
                   * for the patient, prescriber and instruction, the sistema
                   * for what is in the pot (#438).
                   *
                   * Not drawn at present: the rótulo is shown by LabelPanel
                   * and compared with nothing, so no section asks for it.
                   * Kept, with the checks it draws, for when the pharmacy
                   * decides what the label should be held against.
                   */
                  <div className="flex flex-col gap-2">
                    {item.label !== undefined && <LabelPrint lines={item.label.lines} />}
                    {sortLabelFindings(own).map((finding) => {
                      const actions = decidable.includes(finding) ? (
                        <DecisionActions
                          finding={finding}
                          recorded={decisions.get(finding.fingerprint)}
                          busy={busy}
                          onDecision={onDecision}
                          onClear={onClearDecision}
                        />
                      ) : undefined
                      if (labelField(finding) === 'absent') {
                        return (
                          <ComparisonBoard
                            key={finding.fingerprint}
                            status={columnStatusFor(
                              finding.disposition,
                              decisions.has(finding.fingerprint),
                            )}
                          >
                            <RuleRow
                              finding={finding}
                              answered={decisions.has(finding.fingerprint)}
                              actions={actions}
                            />
                          </ComparisonBoard>
                        )
                      }
                      return (
                        <div key={finding.fingerprint} className="flex flex-col gap-2">
                          {/* The same heading the formula's sections wear:
                              on its own step, each field is a section. */}
                          <SectionLabel
                            label={LABEL_FIELD_LABEL[labelField(finding)] ?? 'Rótulo'}
                          />
                          <CheckBoard
                            finding={finding}
                            headings={LABEL_HEADINGS}
                            answered={decisions.has(finding.fingerprint)}
                            located={located}
                            onLocate={onLocate}
                            onPreview={onPreview}
                            previewKey={previewKey}
                            actions={actions}
                          />
                        </div>
                      )
                    })}
                  </div>
                ) : section.label === 'Ativos' ? (
                  <ActivesBoard
                    item={item}
                    focus={focus}
                    decisions={decisions}
                    readOnly={readOnly}
                    answered={new Set(decisions.keys())}
                    located={located}
                    knownProducts={knownProducts}
                    onLocate={onLocate}
                    onPreview={onPreview}
                    previewKey={previewKey}
                    onMarkExcipient={readOnly || busy ? undefined : onMarkExcipient}
                    markable={(ingredientId) => keyedCodes[ingredientId] !== undefined}
                    actionsFor={(rowFindings, onExcipient, rowName) => {
                      const asked = stillOpen(rowFindings.filter((f) => decidable.includes(f)))
                      if (asked.length > 0) {
                        /*
                         * One control for the row, whatever it was flagged on.
                         *
                         * A line can disagree on more than one dimension at
                         * once -- `Fluoxetina 10 mg` keyed as `CLOREXIDINA 2 %`
                         * is an unresolved identity and an incomparable dose --
                         * and a control apiece drew two identical unlabelled
                         * ✓/✗ pairs side by side, with nothing to say which
                         * answered what. The conferente is looking at one line
                         * of the orçamento and answering one question about it;
                         * the tooltip names the dimensions, and each finding is
                         * still recorded on its own fingerprint.
                         *
                         * A rule that asks about this ativo is one of them
                         * (#549), so the reason given for the row is recorded
                         * on the rule's finding as well.
                         *
                         * The finding that can teach the catalogue leads, or
                         * "É um sinônimo" -- which reads the identity finding's
                         * own evidence -- would quietly leave the list whenever
                         * a dose happened to come first.
                         */
                        const primary =
                          asked.find((f) => f.check === 'identity' && canTeach(f)) ??
                          asked.find((f) => canTeach(f, rowName)) ??
                          asked[0]!
                        const rest = asked.filter((f) => f !== primary)
                        return (
                          <DecisionActions
                            key={primary.fingerprint}
                            finding={primary}
                            alongside={rest}
                            rowName={rowName}
                            /*
                             * Pressed only once the whole row is answered.
                             * Rows decided one finding at a time, before the
                             * control was one, are still half-answered in
                             * production; showing those as done would hide
                             * what is left of them.
                             */
                            recorded={
                              asked.every((f) => decisions.has(f.fingerprint))
                                ? decisions.get(primary.fingerprint)
                                : undefined
                            }
                            busy={busy}
                            onDecision={onDecision}
                            onClear={onClearDecision}
                            onExcipient={onExcipient}
                          />
                        )
                      }
                      // Nothing flagged, still answerable: an error the
                      // comparison missed is marked on the row it is on.
                      const quiet = readOnly
                        ? undefined
                        : rowFindings.find(
                            (f) => f.disposition === 'ok' || f.disposition === 'info',
                          )
                      return quiet === undefined ? undefined : (
                        <DecisionActions
                          quiet
                          finding={quiet}
                          recorded={decisions.get(quiet.fingerprint)}
                          busy={busy}
                          onDecision={onDecision}
                          onClear={onClearDecision}
                        />
                      )
                    }}
                  />
                ) : own.length === 0 ? (
                  /* Examined and nothing to say, which is not the same as
                     absent -- and a dash on both sides says so. */
                  <ComparisonBoard status="no_issues">
                    <DiffColumns prescriptionValue="—" systemValue="—" status="no_issues" />
                  </ComparisonBoard>
                ) : compared.length === 0 ? (
                  /* A rule about a part nothing was compared on: there is no
                     row for it to ride, so it stands here as itself. */
                  ruleBoard(rules)
                ) : (
                  compared
                    /*
                     * The first comparison carries the section's rules (#549):
                     * a rule that asks makes it the apontamento, a reminder is
                     * a line under it. In the Correção view it is shown for a
                     * marked rule even when it was not marked itself.
                     */
                    .map((finding, at) => ({
                      finding,
                      riding: at === 0 ? riding : [],
                      reminders: at === 0 ? reminders : [],
                    }))
                    .filter((row) => [row.finding, ...row.riding, ...row.reminders].some(inFocus))
                    .map(({ finding, riding, reminders }) => (
                      <div key={finding.fingerprint} className="flex flex-col">
                        <CheckBoard
                          finding={finding}
                          riding={riding}
                          answeredKeys={answeredKeys}
                          readOnly={readOnly}
                          answered={decisions.has(finding.fingerprint)}
                          located={located}
                          onLocate={onLocate}
                          onPreview={onPreview}
                          previewKey={previewKey}
                          actions={decisionForAll([finding, ...riding])}
                          below={
                            focus === undefined && reminders.length > 0 ? (
                              <ActiveRuleNotes notes={reminders} actionsFor={decisionFor} />
                            ) : undefined
                          }
                        />
                        {asked([finding, ...riding, ...reminders])}
                      </div>
                    ))
                )}

                {/*
                  A decision the rows cannot carry.

                  Composição is about the *set* of ingredients, and the board
                  answers it on the line that broke it: the receita ingredient
                  nobody keyed, the keyed line the receita never named. Drawn
                  here only when no row took it -- otherwise it stood under
                  the table a second time as a bare "Composição" with nothing
                  to say, which read as a check that belonged to no item.
                  Without the fallback an item with no receita side to anchor
                  to would carry a difference nobody could answer, and the
                  conferência could never be finished.
                */}
                {strayCompositionFindings(item)
                  .filter((f) => decidable.includes(f) && own.includes(f))
                  .map((finding) => (
                    <div
                      key={finding.fingerprint}
                      className="flex items-center justify-end gap-2 pr-1"
                    >
                      <span className="text-tSmall text-text-weak">
                        {label(CHECK_LABEL, finding.check)}
                      </span>
                      <DecisionActions
                        finding={finding}
                        recorded={decisions.get(finding.fingerprint)}
                        busy={busy}
                        onDecision={onDecision}
                        onClear={onClearDecision}
                      />
                    </div>
                  ))}
              </div>
            )
          })}
    </div>
  )
}

/**
 * One operational rule that did not pass, in the row shape of everything else.
 *
 * Title, then the sentence, then the same ✓/✗ every other finding is answered
 * with -- a rule is not a special kind of thing to a pharmacist, it is one
 * more item to look at and agree or disagree with.
 *
 * It spans both value columns rather than filling one of them. There is no
 * "na receita" side to a rule about the filial, and putting the text under
 * "Receita" would claim the prescription said it.
 */
/**
 * Which kind of apontamento this is, as a shape.
 *
 * `divergence` and `undecided` both stop a conferência being concluded, and
 * they mean opposite things to a pharmacist: *this is wrong* against *nobody
 * could tell*. The domain keeps them strictly apart -- there is a test in
 * `@rp/core` named for it -- and the screen used to carry the difference in
 * colour alone: red text against amber. Colour alone is also the one channel a
 * colourblind pharmacist does not have, so it is now a shape as well, and the
 * same shape the listing draws.
 *
 * An answered row keeps its mark. What it was is still what it was, and losing
 * the shape on answering would take the distinction away exactly when somebody
 * is reading back over what they decided.
 */
export function DispositionMark({
  disposition,
  answered,
}: {
  disposition: string
  answered: boolean
}) {
  const kind =
    disposition === 'divergence'
      ? 'divergence'
      : disposition === 'undecided'
        ? 'undecided'
        : 'quiet'
  if (kind === 'quiet') return <span aria-hidden="true" />

  const divergence = kind === 'divergence'
  const means = divergence ? DIVERGENCE_MEANS : UNDECIDED_MEANS
  return (
    <Tooltip text={means}>
      <FontAwesomeIcon
        icon={divergence ? icons.alert : icons.undecided}
        aria-label={means}
        className="text-[12px]"
        style={{
          color: answered
            ? 'var(--color-text-weak)'
            : divergence
              ? 'var(--color-text-error)'
              : 'var(--color-text-warning)',
        }}
      />
    </Tooltip>
  )
}

/** One wording for each, so the two screens cannot describe them differently. */
export const DIVERGENCE_MEANS = 'Divergência: os dois lados discordam'
export const UNDECIDED_MEANS = 'Não foi possível comparar'

function RuleRow({
  finding,
  answered,
  actions,
}: {
  finding: Finding
  answered: boolean
  actions?: ReactNode
}) {
  const evidence = (finding.evidence ?? {}) as Record<string, unknown>
  const title = typeof evidence.title === 'string' ? evidence.title : undefined
  const status = columnStatusFor(finding.disposition, answered)
  // From the one place that decides what a finding says. See `reasonFor`.
  const sentence = reasonFor(finding)

  return (
    <ComparisonRow divided>
      {/*
        The pin's column, empty: a rule cites nothing on the page. It used to
        hold the divergência mark, which beside the row's own ⓘ was two red
        icons for one apontamento -- and whose words, "os dois lados
        discordam", described two sides a rule does not have.
      */}
      <span aria-hidden="true" />
      <span className="col-span-2 flex min-w-0 flex-col py-1">
        <span className="flex min-w-0 items-baseline gap-1">
          {/*
            What it found here, on the icon every other finding keeps its
            sentence on. It used to be printed under the title, so a rule was
            the one row of the card that came with a paragraph -- and the card
            is two values and a colour, with the words a hover away. Always
            drawn: with no sentence written, it still says what kind of
            apontamento a rule's is.
          */}
          <ReasonSlot>
            <ReasonIcon reason={ruleHint(finding, sentence)} disposition={finding.disposition} />
          </ReasonSlot>
          <span
            className="text-tP min-w-0 break-words"
            // The colours an ativo's name wears for the same status.
            style={{
              color:
                status === 'error'
                  ? 'var(--color-text-error)'
                  : status === 'warning'
                    ? 'var(--color-text-warning)'
                    : 'var(--color-text-strong)',
              fontWeight: status === 'no_issues' ? 400 : 600,
            }}
          >
            {title ?? label(CHECK_LABEL, finding.check)}
          </span>
        </span>
        {/* Which ativo the rule is about (Galdino, 2026-09-29): the title names
            the rule, this names what it found it on. */}
        {ruleIngredients(evidence).length > 0 && (
          <span className="text-tSmall text-text-strong break-words">
            {ruleIngredients(evidence).length === 1 ? 'Ativo: ' : 'Ativos: '}
            {ruleIngredients(evidence).join(', ')}
          </span>
        )}
      </span>
      <span className="flex w-[4.5rem] justify-end">{actions}</span>
    </ComparisonRow>
  )
}

function CheckBoard({
  finding,
  below,
  riding = [],
  answeredKeys = new Set<string>(),
  headings,
  readOnly = false,
  located,
  onLocate,
  onPreview,
  previewKey,
  actions,
  answered = false,
}: {
  finding: Finding
  /**
   * The rules about this same thing that ask for an answer (#549). They make
   * the row their apontamento: its colour, its stop and its tooltip speak for
   * them too, and the control passed in answers them with it.
   */
  riding?: readonly Finding[]
  /** What has been answered, for the riding rules. */
  answeredKeys?: ReadonlySet<string>
  /** Under the value, inside its board: the reminders about this same thing. */
  below?: ReactNode
  /** Column headings, when the two sides are not the receita and the sistema. */
  headings?: readonly [string, string] | undefined
  /** Whether somebody has already decided this one. Turns the row green. */
  answered?: boolean
  /** Nothing may be answered: the row is not a stop. */
  readOnly?: boolean
  /**
   * The decision, on the row it is about.
   *
   * Passed in rather than built here, for the same reason the pin is: this
   * knows how to lay a difference out and nothing about what may be decided.
   */
  actions?: ReactNode
  located: ReadonlySet<string>
  onLocate:
    | ((key: string | undefined, regions: readonly string[], tone: 'error' | 'brand') => void)
    | undefined
  onPreview?:
    | ((key: string, regions: readonly string[] | undefined, tone: 'error' | 'brand') => void)
    | undefined
  previewKey?: string | undefined
}) {
  // The row and whatever rides on it, answered or not, as one.
  const all = [finding, ...riding]
  const done = new Set([...answeredKeys, ...(answered ? [finding.fingerprint] : [])])
  const tone = worstStatus(all, done)

  return (
    <Locatable
      itemKey={finding.fingerprint}
      regions={finding.regions}
      tone={tone}
      located={located}
      onLocate={onLocate}
      onPreview={onPreview}
      previewed={previewKey === finding.fingerprint}
      needsDecision={
        !readOnly && all.some((f) => needsPerson(f.disposition) && !done.has(f.fingerprint))
      }
      answered={
        !readOnly &&
        all.some((f) => needsPerson(f.disposition)) &&
        all.every((f) => !needsPerson(f.disposition) || done.has(f.fingerprint))
      }
    >
      <DiffColumns
        prescriptionValue={checkSides(finding).prescribed ?? '—'}
        systemValue={checkSides(finding).quoted ?? '—'}
        status={tone}
        headings={headings}
        // A name is wrong by a letter or two, and two bold lines hide which.
        characters={finding.check === 'patient' || finding.check === 'prescriber'}
        /* Every cited field is pinnable, not just the ingredients: a posology,
           a volume, a prescriber's name are all things somebody wants held on
           the page while they read the other side. The rows were already
           clickable and simply never said so. */
        /*
         * Pin and decision share the row's third column, in that order.
         *
         * The pin is about reading and the decision is about answering, so
         * they are separated rather than run together -- but they belong on
         * the same row, because both are about *this* difference and a control
         * that floats away from its subject stops being about anything.
         */
        /*
         * The pin, whatever the disposition.
         *
         * An undecided row used to show its reason *here instead*, so the one
         * row a reader most wants held on the page was the one they could not
         * hold. The reason has its own slot now, in the receita column.
         */
        pin={
          finding.regions.length === 0 ? undefined : (
            <PinCell
              on={located.has(finding.fingerprint)}
              flagged={tone === 'error'}
              previewed={previewKey === finding.fingerprint}
            />
          )
        }
        note={reasonIconFor(all)}
        actions={actions}
        footer={below}
      />
    </Locatable>
  )
}

/**
 * Wraps a row with a way to show it on the prescription.
 *
 * **The row itself is the control.** The first version put a button in the
 * corner, above the row's top edge -- which the board clips, because it is
 * `overflow-hidden` to keep its rounded corners. So on the ingredient list,
 * where the rows live inside the board, the icon was cut away entirely: it
 * could not be seen and could not be clicked, and clicking where it should
 * have been did nothing at all.
 *
 * Making the whole row clickable fixes that and is better anyway. There is
 * nothing else to click on a row, the target is the size of the thing you are
 * reading, and the eye becomes an indicator rather than a small button to
 * find. It sits inside the row's own bounds, so nothing clips it.
 *
 * A row with no cited blocks gets no control at all rather than one that does
 * nothing: an verification can read a field perfectly and cite nothing for it, and
 * offering to locate what cannot be located is worse than not offering.
 */
function Locatable({
  itemKey,
  regions,
  tone,
  located,
  onLocate,
  onPreview,
  previewed = false,
  needsDecision = false,
  answered = false,
  children,
}: {
  itemKey: string
  regions: readonly string[]
  /**
   * Whether somebody still has to answer this row.
   *
   * Where the keys land on a step, and what A and X answer. Stopping on every
   * agreeing line is what makes a keyboard slower than a mouse, so those stay
   * reachable by click only.
   */
  needsDecision?: boolean
  /**
   * A question already answered. Still a stop of the keys, so an answer can
   * be gone back to and taken back with Z; it used to be reachable by mouse
   * only.
   */
  answered?: boolean
  /** What this row concluded. The outline wears the rectangle's colour. */
  tone: ColumnStatus
  located: ReadonlySet<string>
  onLocate:
    | ((key: string | undefined, regions: readonly string[], tone: 'error' | 'brand') => void)
    | undefined
  /**
   * Pointing at a row shows it on the page; it does not choose it.
   *
   * Reading a comparison is a lot of glancing between two panes, and making
   * each glance a click meant either clicking constantly or leaving the wrong
   * row pinned. Hover previews, the pin commits -- and a preview never
   * disturbs what is pinned, so moving the mouse away puts it back.
   */
  onPreview?:
    | ((key: string, regions: readonly string[] | undefined, tone: 'error' | 'brand') => void)
    | undefined
  /** True when the pointer is on this row's blocks, from either side. */
  previewed?: boolean | undefined
  children: ReactNode
}) {
  const on = located.has(itemKey)
  const flagged = tone === 'error'
  if (onLocate === undefined || regions.length === 0) {
    // Named even with nothing to answer and nothing to cite, so something
    // that points at rows -- the adversarial review's tour (#541) -- can find
    // this one. It is not a stop: no focus, no pin.
    if (!needsDecision && !answered) return <div data-review-row={itemKey}>{children}</div>
    /*
     * Nothing to point at on the page, still something to answer.
     *
     * A undecided form -- the receita said "spray", the system said nothing
     * -- cites no block, and this used to hand the row back bare, with no
     * element for the pass to stop on: Tab skipped it and "revisar
     * pendências" had nowhere to land. A stop with no pin is still a stop.
     */
    return (
      <div
        tabIndex={0}
        data-review-row={itemKey}
        data-question="true"
        data-needs-decision={needsDecision ? 'true' : 'false'}
        className={`relative border-l-[3px] border-l-transparent ${FOCUS_RING_ROW}`}
      >
        {children}
      </div>
    )
  }

  /*
   * Always names the row, never `undefined`.
   *
   * It used to send `undefined` to mean "unpin", which the multi-pin handler
   * read as "nothing to do" -- so a second click did nothing and rows could
   * only ever be added. Naming the row and letting the holder toggle keeps
   * the decision in one place.
   */
  const choose = () => onLocate(itemKey, regions, flagged ? 'error' : 'brand')

  return (
    <div
      role="button"
      tabIndex={needsDecision || answered ? 0 : -1}
      data-review-row={itemKey}
      data-question={needsDecision || answered ? 'true' : undefined}
      data-needs-decision={needsDecision ? 'true' : 'false'}
      aria-pressed={on}
      /*
       * Named for assistive technology, with no `title`.
       *
       * The browser's tooltip was appearing underneath ours whenever somebody
       * hovered a decision button — the row is itself a control, so its
       * `title` fired for anything inside it. The row does not need a bubble
       * of its own: it is the size of the thing you are reading, and the pin
       * icon in its own column already says what it does.
       */
      aria-label={on ? 'Desafixar da receita' : 'Fixar na receita'}
      onClick={choose}
      onKeyDown={(event) => {
        // Space pins. Enter on a row still to answer is "corrigir", handled
        // by the screen that owns the pass; on any other row it pins too.
        if (event.key === ' ' || (event.key === 'Enter' && !needsDecision)) {
          event.preventDefault()
          choose()
        }
      }}
      onMouseEnter={() => onPreview?.(itemKey, regions, flagged ? 'error' : 'brand')}
      onMouseLeave={() => onPreview?.(itemKey, undefined, flagged ? 'error' : 'brand')}
      onFocus={() => onPreview?.(itemKey, regions, flagged ? 'error' : 'brand')}
      onBlur={() => onPreview?.(itemKey, undefined, flagged ? 'error' : 'brand')}
      /*
       * The outline is inset and square, so it follows the row's own edges
       * rather than floating around them in a rounded box of its own -- and it
       * wears whatever colour the rectangle on the image is wearing. A row
       * with nothing wrong is worth looking at on the page; drawing its
       * selection in the alarm colour said otherwise.
       *
       * A pinned row hovered gets a tint as well as the outline: without it,
       * pointing at the row that is already pinned looked like nothing had
       * happened, which is exactly when you most want to know the page is
       * showing what you are pointing at.
       */
      /*
       * A bar down the left edge and a tint, not an outline around the row.
       *
       * Outlines were fine one at a time and ugly stacked: two pinned rows
       * next to each other drew four lines between them, which read as a
       * thick divider rather than as two selections. A left bar and a wash
       * abut cleanly, so a run of pinned rows looks like one band -- which is
       * what it is.
       */
      className={`group relative cursor-pointer border-l-[3px] ${FOCUS_RING_ROW} ${
        on
          ? flagged
            ? `border-l-[var(--color-stroke-error-strong)] hover:bg-fill-error-weak/35 ${previewed ? 'bg-fill-error-weak/35' : 'bg-fill-error-weak/20'}`
            : `border-l-[var(--color-stroke-brand-strong)] hover:bg-fill-brand-weak/35 ${previewed ? 'bg-fill-brand-weak/35' : 'bg-fill-brand-weak/20'}`
          : `border-l-transparent hover:bg-fill-weak/40 ${previewed ? 'bg-fill-weak/40' : ''}`
      }`}
    >
      {children}
    </div>
  )
}

/**
 * The pin, in a column of its own.
 *
 * It used to be absolutely positioned over the right edge of the row, which
 * put it on top of the ingredient name whenever the name was long enough to
 * reach. A grid column cannot overlap anything.
 */
/**
 * Where the pin would be, on a row that could not be compared: the reason.
 *
 * There is nothing on the page to point at -- the comparison did not happen
 * because one side was silent -- so a pin would be a control that does
 * nothing. The icon says "this is a question", and the tooltip says which.
 */
/**
 * The icon for a row, from the finding that most deserves one.
 *
 * A row can carry several -- a dose and an identity on the same ingredient --
 * and it has one slot. The worst one wins, because that is the one a
 * pharmacist has to act on.
 *
 * `ok` is deliberately absent. A check that ran and agreed is not an
 * apontamento, and an icon on every row that agreed would put an exclamation
 * beside the rows with nothing to answer -- which is most of them, and which
 * makes the icon stop meaning anything on the rows that do. The slot stays
 * reserved, so those rows still line their values up with the rest.
 */
const REASON_ORDER = ['divergence', 'undecided', 'info']

export function reasonIconFor(findings: readonly Finding[]): ReactNode {
  // A rule on an ativo's row says which rule it is: nothing else there does.
  const reasonOf = (f: Finding) =>
    f.check === 'operational' ? ruleReason(f, reasonFor(f)) : reasonFor(f)
  for (const disposition of REASON_ORDER) {
    const finding = findings.find((f) => f.disposition === disposition)
    if (finding === undefined) continue
    /*
     * A rule is never the one left out (#549). Its enunciado is written
     * nowhere on an ativo's row, so where a comparison finding of the same
     * weight took the slot, the rule's words follow it rather than vanish.
     */
    const rules = findings.filter(
      (f) => f !== finding && f.disposition === disposition && f.check === 'operational',
    )
    const reasons = [finding, ...rules]
      .map(reasonOf)
      .filter((reason): reason is string => reason !== undefined && reason !== '')
    if (reasons.length === 0) continue
    return <ReasonIcon reason={reasons.join(' · ')} disposition={disposition} />
  }
  /*
   * The one exception to "no icon on a row that agreed": a label row the
   * sistema was the reference for, because the receita said nothing. Its
   * receita column is empty, and the icon is the only place that says what
   * the label was actually held against.
   */
  const standIn = findings.find((f) => f.check === 'label' && labelStandIn(f) !== undefined)
  if (standIn !== undefined)
    return <ReasonIcon reason={labelStandIn(standIn)!} disposition="info" />
  return null
}

function PinCell({
  on,
  flagged,
  previewed = false,
}: {
  on: boolean
  flagged: boolean
  /**
   * The pointer is on this row, possibly from the other pane.
   *
   * `group-hover` only fires for a real pointer over this element, so a row
   * lit from the prescription side kept a dim, grey pin -- the one part of
   * the row that did not react, on the row the reader was pointing at.
   */
  previewed?: boolean | undefined
}) {
  return (
    /*
     * The icon carries the words now that the row does not.
     *
     * The row used to hold a `title`, which fired for anything hovered inside
     * it -- including the decision buttons, so the browser's bubble appeared
     * underneath ours. Moving it here puts the explanation on the thing it
     * explains and leaves the rest of the row quiet.
     */
    <Tooltip text={on ? 'Fixado na receita — clique na linha para desafixar' : 'Fixar na receita'}>
      <span
        aria-hidden="true"
        /* self-center, because the row aligns on the baseline for its text and
           a wrapped ingredient left the pin stuck to the first line. */
        className={`flex w-6 self-center justify-center ${
          on || previewed ? 'opacity-100' : 'opacity-30 group-hover:opacity-100'
        }`}
      >
        <FontAwesomeIcon
          icon={icons.pin}
          className={`text-xs ${
            on || previewed
              ? flagged
                ? 'text-icon-error'
                : 'text-icon-brand'
              : 'text-icon-neutral'
          }`}
        />
      </span>
    </Tooltip>
  )
}

/**
 * The ingredient list, both sides, one row each.
 *
 * A row is an ingredient rather than a check: whether the right product was
 * picked and whether its dose agrees are one glance at one line, not two
 * panels. Missing and added ingredients follow underneath, because "this one is
 * absent" is a different sentence from "these two disagree".
 */
function ActivesBoard({
  item,
  located,
  knownProducts,
  onLocate,
  onPreview,
  previewKey,
  actionsFor,
  onMarkExcipient,
  markable = () => false,
  answered = new Set<string>(),
  readOnly = false,
  focus,
  decisions,
}: {
  item: VerificationItem
  readOnly?: boolean
  /** Only the rows holding one of these findings (the Correção view, #439). */
  focus?: ReadonlySet<string> | undefined
  /** What was answered, for the line under a row in focus. */
  decisions?: ReadonlyMap<string, Review> | undefined
  /** Overrides a keyed line's type to excipient. Absent, ✗ does not offer it. */
  onMarkExcipient?:
    | ((ingredientId: string, productName: string, fingerprints: readonly string[]) => void)
    | undefined
  /** Whether we know the product a keyed ingredient was keyed with. */
  markable?: (ingredientId: string) => boolean
  /** Fingerprints already decided, so a finished row reads as finished. */
  answered?: ReadonlySet<string>
  /**
   * The decision for one ingredient row.
   *
   * The ATIVOS rows are built from the item rather than from a finding apiece,
   * so unlike every other section this board cannot reach the controls itself.
   * Passed in for the same reason the pin is: it lays ingredients out and
   * knows nothing about what may be decided about them.
   */
  actionsFor?: (
    findings: readonly Finding[],
    onExcipient: ((fingerprints: readonly string[]) => void) | undefined,
    /** The receita's name on the row, for a synonym taught from a dose (#511). */
    rowName: string | undefined,
  ) => ReactNode
  located: ReadonlySet<string>
  knownProducts: ReadonlySet<string>
  onLocate:
    | ((key: string | undefined, regions: readonly string[], tone: 'error' | 'brand') => void)
    | undefined
  onPreview?:
    | ((key: string, regions: readonly string[] | undefined, tone: 'error' | 'brand') => void)
    | undefined
  previewKey?: string | undefined
}) {
  const board = activesBoard(item)
  const rows =
    focus === undefined
      ? board.rows
      : board.rows.filter((row) =>
          [...row.findings, ...row.notes].some((f) => focus.has(f.fingerprint)),
        )

  /*
   * Recomputed against what has been answered.
   *
   * `activesBoard` bands a row from its findings alone, which is right until
   * somebody starts deciding: from then on the useful question is not "does
   * this disagree" but "is this still mine to do".
   */
  const bandFor = (row: (typeof board.rows)[number]): ColumnStatus =>
    worstStatus(row.findings, answered)

  /*
   * The board says what the section found; each row says what *it* found.
   *
   * Every row used to wear the section's colour, on the reasoning that a
   * differently-coloured line inside a tinted board reads as a second,
   * quieter decision. In practice it did the opposite: one diverging
   * ingredient painted all six of them red, so the row that actually needed
   * looking at was the one thing the colour could not point to.
   *
   * The board keeps its tint -- that is the section's own decision, and it is
   * what draws the eye to the section in the first place.
   */
  const tone = worstStatus(
    board.rows.flatMap((row) => row.findings),
    answered,
  )

  /*
   * One set of buttons per finding. A finding can ride on several rows -- the
   * composition on each ingredient that broke it, a rule on each ativo it
   * names -- but is one answer, so only the first of those rows offers it.
   *
   * Worked out before drawing because the row's stop depends on it too: a row
   * whose findings are all answered elsewhere was still a stop of the keyboard
   * pass, with no control of its own, and X there marked whatever quiet
   * finding the row happened to hold.
   */
  const offeredOn = new Map<string, string>()
  for (const row of rows) {
    for (const f of row.findings) {
      if (!offeredOn.has(f.fingerprint)) offeredOn.set(f.fingerprint, row.key)
    }
  }
  /*
   * Except the composition, which is offered on every line that broke it.
   *
   * Each ingredient the receita named and nobody keyed is its own row, and
   * offered once the answer sat on the first: on 50/302214 Caryophyllus had
   * ✓/✗ and Artemisia, missing just the same, had nothing -- which read as a
   * line that could not be answered. Every copy records the same decision, so
   * answering on either row answers both and both read as answered.
   */
  const firstOn = (row: (typeof board.rows)[number]) =>
    row.findings.filter((f) => offeredOn.get(f.fingerprint) === row.key)
  const freshOn = (row: (typeof board.rows)[number]) =>
    row.findings.filter(
      (f) => f.check === 'composition' || offeredOn.get(f.fingerprint) === row.key,
    )
  const actionsOn = (row: (typeof board.rows)[number]) => {
    const fresh = freshOn(row)
    /*
     * "É excipiente" under ✗, only on a keyed line the receita never named:
     * the one row that can be an excipient the ERP typed as an ingredient.
     * A reason rather than a control of its own, so the row stays as quiet as
     * every other (ADR 0014).
     */
    const keyed = row.keyedIngredientId
    const name = row.quotedName
    const onExcipient =
      onMarkExcipient !== undefined && keyed !== undefined && name !== undefined && markable(keyed)
        ? (fingerprints: readonly string[]) => onMarkExcipient(keyed, name, fingerprints)
        : undefined
    return fresh.length === 0 ? undefined : actionsFor?.(fresh, onExcipient, row.name)
  }

  // The icon's slot is held open only where some row has an icon to put in it.
  const anyReason = board.rows.some((row) => reasonIconFor(row.findings) !== null)

  return (
    <ComparisonBoard status={tone} fillRows>
      <ComparisonHeader status={tone} />

      {rows.map((row) => (
        <Fragment key={row.key}>
          <Locatable
            itemKey={row.key}
            regions={row.regions}
            tone={bandFor(row)}
            located={located}
            onLocate={onLocate}
            onPreview={onPreview}
            previewed={previewKey === row.key}
            needsDecision={
              !readOnly &&
              freshOn(row).some(
                (f) =>
                  (f.disposition === 'divergence' || f.disposition === 'undecided') &&
                  !answered.has(f.fingerprint),
              )
            }
            answered={
              !readOnly &&
              freshOn(row).some((f) => needsPerson(f.disposition)) &&
              freshOn(row).every((f) => !needsPerson(f.disposition) || answered.has(f.fingerprint))
            }
          >
            <ComparisonRow divided className={rowFill(bandFor(row))}>
              {/* The pin, on every row. It used to be traded away for the
                reason icon on the rows that could not be checked, which are
                exactly the rows somebody wants to hold on the page. */}
              <PinCell
                on={located.has(row.key)}
                flagged={bandFor(row) === 'error'}
                previewed={previewKey === row.key}
              />
              {/* The reason leads the receita column in a slot of its own, so an
                ativo with an icon and one without start their names together. */}
              <span className="flex min-w-0 items-baseline gap-1">
                {anyReason && <ReasonSlot>{reasonIconFor(row.findings)}</ReasonSlot>}
                {/* Uppercased like the ERP side beside it, so the two columns
                  scan alike. The name only: the dose keeps its units as read. */}
                <ActiveCell
                  text={joinActive(row.name?.toLocaleUpperCase('pt-BR'), row.prescribedDose)}
                  status={bandFor(row)}
                />
              </span>
              <span className="flex min-w-0 flex-col">
                <ActiveCell
                  text={joinActive(row.quotedName, row.quotedDose)}
                  status={bandFor(row)}
                  // Only when we hold it: a code the catalogue has never seen is
                  // a link to a not-found page.
                  erpCode={
                    row.quotedErpCode !== undefined && knownProducts.has(row.quotedErpCode)
                      ? row.quotedErpCode
                      : undefined
                  }
                />
              </span>
              <span className="flex w-[4.5rem] justify-end">{actionsOn(row)}</span>
            </ComparisonRow>
            {focus !== undefined && decisions !== undefined && (
              <CorrectionAsked
                // Once, under the row that answers it, not under every ativo a rule names.
                findings={[...firstOn(row), ...row.notes].filter((f) => focus.has(f.fingerprint))}
                decisions={decisions}
              />
            )}
          </Locatable>
          {/*
            Outside the row: the keyboard answers a row by pressing the first
            ✓ or ✗ inside it, and on an ativo with nothing flagged that would
            be the reminder's. Not in the Correção view, which shows only what
            was marked -- a reminder marked there is the line above instead.
          */}
          {focus === undefined && (
            <ActiveRuleNotes
              notes={row.notes}
              actionsFor={(note) => actionsFor?.([note], undefined, undefined)}
            />
          )}
        </Fragment>
      ))}
    </ComparisonBoard>
  )
}

/**
 * The wash a row wears, now that the board no longer wears it for everyone.
 *
 * Translucent, because the row sits inside the element that tints itself when
 * pinned or pointed at. An opaque fill here would paint over that, and a
 * diverging row would be the one row whose pin you could not see.
 */
function rowFill(status: ColumnStatus): string {
  if (status === 'error') return 'bg-fill-error-weak/70'
  if (status === 'warning') return 'bg-fill-warning-weak/70'
  if (status === 'resolved') return 'bg-fill-success-weak/70'
  return ''
}

function joinActive(name: string | undefined, dose: string | undefined): string | undefined {
  // "MINOXIDIL 1MG" already says 1 mg; saying it twice reads as two things.
  const squash = (text: string) => text.toLowerCase().replace(/[\s.,]/g, '')
  const repeated = name !== undefined && dose !== undefined && squash(name).includes(squash(dose))
  const parts = [name, repeated ? undefined : dose].filter(
    (part): part is string => part !== undefined && part !== '',
  )
  return parts.length === 0 ? undefined : parts.join('  ')
}

/** An absent side is an em dash in italics, never a blank cell. */
/*
 * Both columns start where their headings do.
 *
 * The system side carried a `pl-5` that nothing above it did -- not the
 * "No sistema" heading, and not `DiffLine`, which lays out the same two
 * columns on the same screen. So every ingredient in the composição board sat
 * twenty pixels right of the heading naming it, and right of the value it is
 * being compared against, on a screen whose entire job is putting two columns
 * beside each other.
 *
 * The grid's own `gap-x-3` is what separates the columns, and `DiffColumns`
 * has always read fine on that alone.
 */
function ActiveCell({
  text,
  status,
  erpCode,
}: {
  text: string | undefined
  status: ColumnStatus
  /**
   * The product selected in the ERP, when identity resolved *and* we hold it.
   *
   * Present turns the name into a link to that product's page, where its
   * synonyms live. Absent leaves plain text -- a link to nowhere is worse than
   * no link, and the code is unknown whenever identity never ran, or known to
   * the ERP but never synced here, or a simulation's, which resolves to
   * nothing at all.
   */
  erpCode?: string | undefined
}) {
  if (text === undefined) {
    return (
      <span aria-label="ausente" className="text-tP text-text-weak min-w-0 italic">
        —
      </span>
    )
  }

  const emphasised = status !== 'no_issues'
  const content = (
    <span
      title={text}
      /*
       * Wrapped, never truncated.
       *
       * "BIOPEROLAS LA VIE EST BELLE 3…" hides the very characters somebody is
       * comparing -- an ingredient name that differs only at its end reads as
       * identical once the end is an ellipsis. A tall row costs a little
       * space; a hidden difference costs the point of the screen.
       */
      className="text-tP min-w-0 py-1 break-words"
      style={{
        color:
          status === 'error'
            ? 'var(--color-text-error)'
            : status === 'warning'
              ? 'var(--color-text-warning)'
              : 'var(--color-text-strong)',
        fontWeight: emphasised ? 600 : 400,
      }}
    >
      {text}
    </span>
  )

  /*
   * The catalogue is ours to look at.
   *
   * `/api/synonyms/*` answers 404 to anybody below super admin, so this link
   * would take a pharmacist to a screen that cannot load -- and tell them a
   * surface exists that is not theirs. The name stays as plain text for them,
   * which is what it already does whenever the code is unknown.
   */
  if (erpCode === undefined || !sessionIsSuperAdmin()) return content

  return (
    <a
      href={`/products/${encodeURIComponent(erpCode)}`}
      // Stops the row's own locate control from firing: clicking a product
      // name means "show me the product", not "highlight this on the page".
      onClick={(event) => event.stopPropagation()}
      className="min-w-0 underline decoration-dotted underline-offset-4"
      title={`Ver ${text} no catálogo`}
    >
      {content}
    </a>
  )
}

/** The card's own band, for callers that sort or tint by it. */
export function cardStatus(findings: readonly Finding[]) {
  return worstStatus(findings)
}
